import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import dayjs from 'dayjs';
import filter from 'lodash/filter';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ROUTES } from '../../../helpers/constants';
import WebAPIClient from '../../../api/_energytracer';

// ─── StatItem ─────────────────────────────────────────────────────────────────

function StatItem({ label, value, loading }) {
  return (
    <Box sx={{ textAlign: 'center', px: 1.5 }}>
      {loading ? (
        <Skeleton variant='text' width={40} height={36} sx={{ mx: 'auto' }} />
      ) : (
        <Typography variant='h5' fontWeight={700} sx={{ lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
      )}
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: 600,
          fontSize: 10,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ─── AppCard ──────────────────────────────────────────────────────────────────

function AppCard({ iconName, title, route, stats, loading, accentColor }) {
  const dispatch = useDispatch();

  return (
    <Card
      variant='outlined'
      sx={{ borderTop: `3px solid ${accentColor}`, height: '100%' }}
    >
      <CardActionArea onClick={() => dispatch(push(route))} sx={{ p: 0 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Box
              component='img'
              src={`/icons/${iconName}-icon.png`}
              alt={title}
              sx={{ height: 32, width: 32, objectFit: 'contain' }}
            />
            <Typography variant='h6' fontWeight={700}>
              {title}
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Stack
            direction='row'
            flexWrap='wrap'
            divider={<Divider orientation='vertical' flexItem />}
          >
            {stats.map((stat) => (
              <StatItem
                key={stat.label}
                label={stat.label}
                value={stat.value}
                loading={loading}
              />
            ))}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ─── Home ─────────────────────────────────────────────────────────────────────

export default function Home() {
  const {
    loading: demandLoading,
    organizations: demandOrgs,
    sites,
    meters,
    alarms: demandAlarms,
    users: demandUsers,
  } = useSelector((state) => state.demand);
  const {
    loading: solarLoading,
    organizations: solarOrgs,
    loggers,
    alarms: solarAlarms,
    users: solarUsers,
  } = useSelector((state) => state.solar);
  const {
    loading: mgmtLoading,
    organizations: mgmtOrgs,
    users: mgmtUsers,
  } = useSelector((state) => state.management);

  const gridcapStats = useMemo(() => {
    const thirtyDaysAgo = dayjs().subtract(30, 'day');
    return [
      { label: 'Orgs', value: demandOrgs.length },
      { label: 'Sites', value: sites.length },
      { label: 'Meters', value: meters.length },
      {
        label: 'Active Alarms',
        value: filter(demandAlarms, (a) => a.status).length,
      },
      {
        label: 'Active Users',
        value: filter(demandUsers, (u) => dayjs(u.last_update) > thirtyDaysAgo)
          .length,
      },
    ];
  }, [demandOrgs, sites, meters, demandAlarms, demandUsers]);

  const lightlevelStats = useMemo(() => {
    const thirtyDaysAgo = dayjs().subtract(30, 'day');
    return [
      { label: 'Orgs', value: solarOrgs.length },
      { label: 'Loggers', value: loggers.length },
      { label: 'Online', value: filter(loggers, (l) => l.online).length },
      {
        label: 'Active Alarms',
        value: filter(solarAlarms, (a) => a.status).length,
      },
      {
        label: 'Active Users',
        value: filter(solarUsers, (u) => dayjs(u.last_update) > thirtyDaysAgo)
          .length,
      },
    ];
  }, [solarOrgs, loggers, solarAlarms, solarUsers]);

  const eventsyncStats = useMemo(
    () => [
      { label: 'Orgs', value: mgmtOrgs.length },
      { label: 'Users', value: mgmtUsers.length },
    ],
    [mgmtOrgs, mgmtUsers]
  );

  const [etLoading, setEtLoading] = useState(true);
  const [etStats, setEtStats] = useState([
    { label: 'Orgs', value: null },
    { label: 'Users', value: null },
    { label: 'Connections', value: null },
  ]);

  const fetchEnergyTracer = useCallback(async () => {
    try {
      const data = await new WebAPIClient().GET('/kpis/home');
      setEtStats([
        { label: 'Orgs', value: data.org_count ?? '—' },
        { label: 'Users', value: data.user_count ?? '—' },
        { label: 'Connections', value: data.active_connections ?? '—' },
      ]);
    } catch {
      setEtStats([
        { label: 'Orgs', value: '—' },
        { label: 'Users', value: '—' },
        { label: 'Connections', value: '—' },
      ]);
    } finally {
      setEtLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnergyTracer();
  }, [fetchEnergyTracer]);

  return (
    <Box
      sx={{ maxWidth: 1000, width: '100%', mx: 'auto', px: 2, mt: 3, mb: 5 }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <AppCard
            iconName='gridcap'
            title='Gridcap'
            route={ROUTES.GRIDCAP.path}
            stats={gridcapStats}
            loading={demandLoading}
            accentColor='#1976d2'
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <AppCard
            iconName='lightlevel'
            title='LightLevel'
            route={ROUTES.LIGHTLEVEL.path}
            stats={lightlevelStats}
            loading={solarLoading}
            accentColor='#f59e0b'
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <AppCard
            iconName='hub'
            title='EventSync'
            route={ROUTES.EVENTSYNC.path}
            stats={eventsyncStats}
            loading={mgmtLoading}
            accentColor='#10b981'
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <AppCard
            iconName='energytracer'
            title='EnergyTracer'
            route={ROUTES.ENERGYTRACER.path}
            stats={etStats}
            loading={etLoading}
            accentColor='#8b5cf6'
          />
        </Grid>
      </Grid>
    </Box>
  );
}
